import { Injectable, inject } from '@angular/core';
import { ChartConstants } from '@modules/apc/apc/data/chart-modules';
import { ReportConfig } from '@modules/report-generator/interfaces';
import { Result, errResult, okResult } from '@shared/helpers/result-type.helper';
import { CustomRendererService } from '@shared/services/custom-renderer.service';

declare let Chart: any;

interface GaugeChartItem {
  key: string;
  min: number;
  max: number;
  current: number;
  title: string;
  value?: string;
  unit?: string;
  prefix?: string;
  value_alternative_1?: string;
  unit_alternative_1?: string;
  prefix_alternative_1?: string;
  value_alternative_2?: string;
  unit_alternative_2?: string;
  prefix_alternative_2?: string;
  value_alternative_3?: string;
  unit_alternative_3?: string;
  prefix_alternative_3?: string;
  is_heart_rate: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ReportMetabolicCapacitiesGridService {
  private readonly customRendererService: CustomRendererService = inject(CustomRendererService);

  destroyChartsForSuffix(suffix: string): void {
    const canvases = document.querySelectorAll(`canvas[id$="${suffix}"]`);
    canvases.forEach((canvas: any) => {
      if (canvas.chart) {
        canvas.chart.destroy();
        canvas.chart = null;
      }
    });
  }

  renderMetabolicCapacitiesGrid(metabolicPlot1: Record<string, any>, suffix?: string, config?: ReportConfig): void {
    const gaugeChartData = this.generateGaugeChartData(metabolicPlot1, suffix);
    this.generateMetabolicCapacitiesGaugeChart(gaugeChartData, config);
  }

  renderMetabolicCapacitiesSingle(
    metabolicPlot1: Record<string, any>,
    subtype: string,
    config: ReportConfig,
    suffix: string
  ): void {
    const plot = metabolicPlot1.plots.find((p: Record<string, any>) => p.type === subtype);
    if (!plot) {
      console.error(`Plot type ${subtype} not found in response.`);
      return;
    }
    const gaugeChartItem = this.generateGaugeChartItem(plot, suffix);
    this.generateMetabolicCapacitiesGaugeChart([gaugeChartItem], config);
  }

  generateGaugeChartData(metabolicPlot1: Record<string, any>, suffix?: string): Array<GaugeChartItem> {
    return metabolicPlot1.plots.map((plot: Record<string, any>) => this.generateGaugeChartItem(plot, suffix), this);
  }

  generateGaugeChartDataSingle(
    metabolicPlot1: Record<string, any>,
    subtype: string,
    suffix: string
  ): Result<Array<GaugeChartItem>, string> {
    const plot = metabolicPlot1.plots.find((p: Record<string, any>) => p.type === subtype);
    if (!plot) {
      console.error(`Plot type ${subtype} not found in response.`);
      // TOOD: Error should be non-blocking
      // return errResult(`Metric ${subtype} not found`);
    }
    return okResult([this.generateGaugeChartItem(plot, suffix)]);
  }

  private generateGaugeChartItem(currentPlot: Record<string, any>, suffix: string = ''): GaugeChartItem {
    const gaugeChartItem = {
      key: `${currentPlot.type}${suffix}`,
      min: 0,
      max: 100,
      current: currentPlot.circles[0].source.percentage[0],
      title: currentPlot.title_text,
      value: undefined,
      unit: undefined,
      prefix: undefined,
      value_alternative_1: undefined,
      unit_alternative_1: undefined,
      prefix_alternative_1: undefined,
      value_alternative_2: undefined,
      unit_alternative_2: undefined,
      prefix_alternative_2: undefined,
      value_alternative_3: undefined,
      unit_alternative_3: undefined,
      prefix_alternative_3: undefined,
      is_heart_rate:
        currentPlot.title_text.toLowerCase().indexOf('heart rate') > -1 && !currentPlot.circles[1].source.feature,
    };

    // Parse prefix / value / unit
    const values = (currentPlot.circles[0].source.names[0] || '').split(' ');
    let isValueContainsPrefix =
      currentPlot.circles[0].source.names[0].indexOf('elative') > -1 ||
      currentPlot.circles[0].source.names[0].indexOf('otal') > -1;
    if (currentPlot.circles[0].source.feature) {
      isValueContainsPrefix = true;
    }
    const param = {
      prefixField: 'prefix',
      valueField: 'value',
      unitField: 'unit',
    };
    this._parse_Prefix_Value_Unit(gaugeChartItem, values, isValueContainsPrefix, param);

    if (currentPlot.circles.length > 1) {
      // Parse prefix / value / unit
      const values_alt = (currentPlot.circles[1].source.names[0] || '').split(' ');
      isValueContainsPrefix =
        currentPlot.circles[1].source.names[0].indexOf('elative') > -1 ||
        currentPlot.circles[0].source.names[0].indexOf('otal') > -1;
      if (currentPlot.circles[1].source.feature) {
        isValueContainsPrefix = true;
      }
      const param_atl = {
        prefixField: 'prefix_alternative_1',
        valueField: 'value_alternative_1',
        unitField: 'unit_alternative_1',
      };
      this._parse_Prefix_Value_Unit(gaugeChartItem, values_alt, isValueContainsPrefix, param_atl);
    }
    if (currentPlot.circles.length > 2) {
      // Parse prefix / value / unit
      const values_alt = (currentPlot.circles[2].source.names[0] || '').split(' ');
      isValueContainsPrefix =
        currentPlot.circles[2].source.names[0].indexOf('elative') > -1 ||
        currentPlot.circles[0].source.names[0].indexOf('otal') > -1;
      if (currentPlot.circles[2].source.feature) {
        isValueContainsPrefix = true;
      }
      const param_atl = {
        prefixField: 'prefix_alternative_2',
        valueField: 'value_alternative_2',
        unitField: 'unit_alternative_2',
      };
      this._parse_Prefix_Value_Unit(gaugeChartItem, values_alt, isValueContainsPrefix, param_atl);
    }
    if (currentPlot.circles.length > 3) {
      // Parse prefix / value / unit
      const values_alt = (currentPlot.circles[3].source.names[0] || '').split(' ');
      isValueContainsPrefix =
        currentPlot.circles[3].source.names[0].indexOf('elative') > -1 ||
        currentPlot.circles[0].source.names[0].indexOf('otal') > -1;
      if (currentPlot.circles[3].source.feature) {
        isValueContainsPrefix = true;
      }
      const param_atl = {
        prefixField: 'prefix_alternative_3',
        valueField: 'value_alternative_3',
        unitField: 'unit_alternative_3',
      };
      this._parse_Prefix_Value_Unit(gaugeChartItem, values_alt, isValueContainsPrefix, param_atl);
    }

    return gaugeChartItem;
  }

  private _parse_Prefix_Value_Unit(
    gaugeChartItem: any,
    values: any,
    isValueContainsPrefix: boolean,
    param: { prefixField: string; valueField: string; unitField: string }
  ): void {
    if (values.length >= 3 && isValueContainsPrefix) {
      let prefix = values[0].replace(/_/g, ' ');
      if (values[0] === 'VLamax:') {
        prefix = 'VLa<sub>max</sub>:';
      }
      if (values[0] === '%VO2max:') {
        prefix = '%VO2<sub>max</sub>:';
      }
      if (values[0] === 'Carbmax:') {
        prefix = 'Carb<sub>max</sub>:';
      }
      if (values[0] === 'Fatmax:') {
        prefix = 'Fat<sub>max</sub>:';
      }
      gaugeChartItem[param.prefixField] = prefix;
      gaugeChartItem[param.valueField] = values[1];
      gaugeChartItem[param.unitField] = values.slice(2).join(' ');
    } else if (values.length >= 3 && !isValueContainsPrefix) {
      gaugeChartItem[param.valueField] = values[0];
      gaugeChartItem[param.unitField] = values.slice(1).join(' ');
    } else if (values.length == 2) {
      gaugeChartItem[param.valueField] = values[0];
      gaugeChartItem[param.unitField] = values[1];
    } else {
      gaugeChartItem[param.valueField] = values[0];
    }
  }

  private generateMetabolicCapacitiesGaugeChart(gaugeChartItems: Array<GaugeChartItem>, config?: ReportConfig): void {
    const primary = config?.template?.theme?.colors?.primary;
    const secondary = config?.template?.theme?.colors?.secondary;

    gaugeChartItems.forEach((item) => {
      const elementId = `#gauge-${item.key}`;
      let ctx: any = this.customRendererService.select(elementId);
      if (!ctx) {
        return;
      }

      // Destroy existing chart if it exists
      if (ctx.chart) {
        ctx.chart.destroy();
      }

      let context = ctx.getContext('2d');
      let backgroundColor = [secondary ?? ChartConstants.COLOR.RED, primary ?? ChartConstants.COLOR.BLUE];

      const newChart = new Chart(context, {
        type: 'gauge',
        data: {
          datasets: [
            {
              value: item.current,
              minValue: item.min,
              data: [item.current, item.max],
              backgroundColor: backgroundColor,
              borderWidth: 0,
            },
          ],
        },
        options: {
          animation: {
            duration: 0,
          },
          cutoutPercentage: 65,
          needle: {
            radiusPercentage: 1,
            widthPercentage: 1.5,
            lengthPercentage: 80,
            color: '#888888',
          },
          valueLabel: {
            display: false,
          },
          layout: {
            padding: {
              bottom: 8,
            },
          },
        },
      });

      // Store the new instance so it can be destroyed next time
      ctx.chart = newChart;
    });
  }
}
